import { useEffect, useMemo, useState } from 'react';
import { getAllMovieSubmissions } from '../../../lib/movieSubmissions';
import { ALLOWED_COLUMNS, DEFAULT_FAVORITE_CATEGORY_ORDER, FAVORITES_COLUMN } from '../constants';
import { formatCategoryLabel, normalizeCategoryKey } from '../categoryUtils';

export const useMovieSubmissions = () => {
  const [favoriteSubmissions, setFavoriteSubmissions] = useState([]);
  const [favoritesLoadStatus, setFavoritesLoadStatus] = useState('loading');

  useEffect(() => {
    let isMounted = true;

    const loadMovieSubmissions = async () => {
      setFavoritesLoadStatus('loading');

      try {
        const submissions = await getAllMovieSubmissions();
        if (!isMounted) {
          return;
        }

        setFavoriteSubmissions(submissions);
        setFavoritesLoadStatus('ready');
      } catch (_error) {
        if (!isMounted) {
          return;
        }

        setFavoritesLoadStatus('error');
      }
    };

    loadMovieSubmissions();

    return () => {
      isMounted = false;
    };
  }, []);

  const submissionsByColumn = useMemo(() => {
    const nextSubmissionsByColumn = {
      favoritos: [],
      assistidos: [],
      'quero-ver': [],
    };

    favoriteSubmissions.forEach((submission) => {
      const normalizedColumn = ALLOWED_COLUMNS.includes(submission.column)
        ? submission.column
        : FAVORITES_COLUMN;
      const normalizedSubmission = {
        ...submission,
        column: normalizedColumn,
        categoryKey: normalizeCategoryKey(submission.categoryKey || submission.category),
      };

      nextSubmissionsByColumn[normalizedColumn].push({
        ...normalizedSubmission,
        category: formatCategoryLabel(normalizedSubmission.categoryKey, submission.category),
      });
    });

    return nextSubmissionsByColumn;
  }, [favoriteSubmissions]);

  const favoritesByCategory = useMemo(() => {
    const categories = {};

    submissionsByColumn[FAVORITES_COLUMN].forEach((submission) => {
      const categoryKey = submission.categoryKey || 'geral';
      if (!categories[categoryKey]) {
        categories[categoryKey] = [];
      }
      categories[categoryKey].push(submission);
    });

    DEFAULT_FAVORITE_CATEGORY_ORDER.forEach((categoryKey) => {
      if (!categories[categoryKey]) {
        categories[categoryKey] = [];
      }
    });

    return categories;
  }, [submissionsByColumn]);

  const favoriteSectionKeys = useMemo(() => {
    const dynamicFavoriteCategoryKeys = Object.keys(favoritesByCategory)
      .filter((categoryKey) => !DEFAULT_FAVORITE_CATEGORY_ORDER.includes(categoryKey))
      .sort((left, right) => {
        const leftLabel = formatCategoryLabel(left);
        const rightLabel = formatCategoryLabel(right);
        return leftLabel.localeCompare(rightLabel, 'pt-BR');
      });

    return [...DEFAULT_FAVORITE_CATEGORY_ORDER, ...dynamicFavoriteCategoryKeys];
  }, [favoritesByCategory]);

  return {
    favoritesLoadStatus,
    submissionsByColumn,
    favoritesByCategory,
    favoriteSectionKeys,
  };
};
